function containsHtml(value: string) {
  return /<[^>]+>/.test(value)
}

const MARKDOWN_IMAGE_REGEXP = /!\[([^\]]*)\]\(([^)]+)\)/g
const HTML_IMAGE_REGEXP = /<img\b[^>]*>/gi

function getHtmlImageSource(imageMarkup: string) {
  return imageMarkup.match(/\bsrc=(?:"([^"]+)"|'([^']+)'|([^\s>]+))/i)?.slice(1).find(Boolean) || ''
}

export function getRichTextPlainText(content: string) {
  if (!content) {
    return ''
  }

  if (containsHtml(content)) {
    if (typeof window === 'undefined') {
      return content.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim()
    }

    const documentFragment = new DOMParser().parseFromString(content, 'text/html')
    return documentFragment.body.textContent?.replace(/\u00a0/g, ' ').replace(/\s+/g, ' ').trim() || ''
  }

  return content
    .replace(MARKDOWN_IMAGE_REGEXP, ' $1 ')
    .replace(/\[([^\]]+)\]\(([^)]+)\)/g, ' $1 ')
    .replace(/[`*_>#-]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
}

export function isRichTextEmpty(content: string) {
  if (!content) {
    return true
  }

  if (containsHtml(content) && /<img\b/i.test(content)) {
    return false
  }

  if (/!\[[^\]]*\]\(([^)]+)\)/.test(content)) {
    return false
  }

  return !getRichTextPlainText(content)
}

export function replaceRichTextImageSources(
  content: string,
  sourceMap: Map<string, string>,
) {
  if (!content || !sourceMap.size) {
    return content
  }

  return content
    .replace(HTML_IMAGE_REGEXP, (fullMatch) => {
      const currentSource = getHtmlImageSource(fullMatch)
      const nextSource = currentSource ? sourceMap.get(currentSource) : null
      return nextSource ? fullMatch.replace(currentSource, nextSource) : fullMatch
    })
    .replace(MARKDOWN_IMAGE_REGEXP, (fullMatch, altText, currentSource) => {
      const nextSource = sourceMap.get(currentSource)
      return nextSource ? `![${altText}](${nextSource})` : fullMatch
    })
}

export function removeRichTextImageSources(content: string, sources: string[]) {
  if (!content || !sources.length) {
    return content
  }

  const sourceSet = new Set(sources)

  return content
    .replace(HTML_IMAGE_REGEXP, (fullMatch) => {
      const currentSource = getHtmlImageSource(fullMatch)
      return currentSource && sourceSet.has(currentSource) ? '' : fullMatch
    })
    .replace(MARKDOWN_IMAGE_REGEXP, (fullMatch, _altText, currentSource) =>
      sourceSet.has(currentSource) ? '' : fullMatch,
    )
}

export function extractRichTextImageSources(content: string) {
  if (!content) {
    return []
  }

  return [
    ...Array.from(content.matchAll(HTML_IMAGE_REGEXP))
      .map((match) => getHtmlImageSource(match[0]))
      .filter(Boolean),
    ...Array.from(content.matchAll(MARKDOWN_IMAGE_REGEXP)).map((match) => match[2]),
  ]
}
